import merge from "lodash/merge";
import regTranslation from "./../registration/config/translations";
import uplvlRenewTranslation from "../uplevel_renewal/config/translations";
import managePartnershipTranslation from "../managepartnership/config/translations";
import specializationTranslation from "../specialization/config/translations";
import { fallbackLocale } from "./config";

function localizedLink(linkKey, locale) {
  return window.apcConfig?.localized_links?.[linkKey]?.[locale] ?? "";
}

function apcDirectLogin() {
  return window.apcConfig?.partner?.apc_direct_login ?? "";
}

let translations = {
  en_US: {
    global: {
      ims: {
        sign_in: "Sign in with Adobe ID",
        sign_out: "Sign Out",
      },
      header: {
        app_prefix_bold: "Adobe",
        // app_name: 'Partner Connection Contact Registration',
        app_name: "Adobe Partner Connection Portal",
        app_prefix_bold_contact_approved: "Adobe Partners",
        app_name_contact_approved: "Adobe Partner Connection",
      },
      footer: {
        region: "Region:",
        choose_region: "Choose your region",
        privacy: "Privacy",
        privacy_link: localizedLink("privacy_link", "en_US"),
        terms_of_use: "Terms of Use",
        terms_of_use_link: localizedLink("terms_of_use_link", "en_US"),
        cookies: "Cookies",
        cookies_link: localizedLink("cookies_link", "en_US"),
        help: "Helpdesk",
        help_link: localizedLink("help_link", "en_US"),
      },
      errors: {
        unable_to_load: "Unable to load the page",
        unauthorized: "You are not authorized to view this page",
      },
    },
    workflow: {
      progress: {
        sign_in: "Sign In",
        select_company: "Select Company",
        personal_information: "Personal Information",
        finish: "Finish",
      },
      error_page: {
        problem_header: "There was a problem.",
        general_problem:
          "An unknown problem occured while trying to complete your request. Please contact the helpdesk so we can further assist you.",
        associated_other_account:
          "The Adobe ID you've signed in with (%{email}) is already associated with another Adobe Partner program: %{program}. You can sign in with another Adobe ID, or you can contact the helpdesk to continue your registration.",
        account_not_found:
          "The Adobe ID you've signed in with (%{email}) does not match any current Adobe Partner Connection accounts. You can apply to join the Adobe Partner Connection program or sign in with a different Adobe ID.",
        initial_search_problem:
          "There was an issue while trying to look up your information. You can try refreshing the page to try the request again, or contact the helpdesk.",
        company_search_problem:
          "There was an issue looking up your company search results. You can try searching again, or you can contact the helpdesk.",
        admin_fetch_requests_problem:
          "There was an issue while trying to load the pending access requests. You can try refreshing the page to try the request again, or contact the helpdesk.",
        admin_action_problem:
          "There was a problem while trying to take action on this request. Please try again. If you continue to experience issues, please contact the helpdesk.",
        submit_request_problem:
          "There was a problem while trying to submit your request. Please try submitting the form again. If you continue to experience issues, please contact the helpdesk.",
        banned_domain:
          "The Adobe ID you're signed in with (%{email}) is a public domain that cannot be used to register for the Adobe Partner Connection program. Please sign in with your company provided e-mail address or contact our helpdesk for assistance.",
        already_registered:
          "The Adobe ID you've signed in with (%{email}) is already registered for the Adobe Partner Connection program. You can sign in via the links below.",
        associated_non_partner_account:
          "The Adobe ID you've signed in with (%{email}) is already associated with another account. You can sign in with another Adobe ID, or please contact the helpdesk to continue your registration.",
        rejected_contact:
          "The Adobe ID you've signed in with (%{email}) is not allowed to register for the Adobe Partner Connection program at this time. Please contact the helpdesk for assistance.",
        partial_registration:
          "The account you're associated with hasn't yet completed the enrollment process. Before you can continue with Partner Connection contact registration, please finish the account enrollment. If you're having issues with enrollment, please contact the helpdesk for assistance.",
        contact_helpdesk: "Contact Helpdesk",
        apply_join: "Apply to join",
        ok: "OK",
        cbc_button_text: "Consumer & Business Connection",
        cbc_link: localizedLink("cbc_link", "en_US"),
        apc_button_text: "Adobe Partner Connection",
        apc_link: localizedLink("apc_link", "en_US"),
        apc_direct_login: apcDirectLogin(),
      },
      personal_information: {
        header: "Personal information",
        first_name: "First name",
        last_name: "Last name",
        email_address: "E-mail address",
        phone_number: "Phone number",
        phone_number_placeholder: "Enter your phone number",
        job_role: "Job role",
        job_role_placeholder: "Select a job role",
        role_accounting: "Accounting",
        role_administration: "Administration",
        role_development: "Development",
        role_executive: "Executive",
        role_finance: "Finance",
        role_helpdesk_support: "Helpdesk Support",
        role_it: "IT",
        role_marketing: "Marketing",
        role_owner: "Owner",
        role_partner_specialist: "Partner Specialist",
        role_pr_communications: "PR/Communications",
        role_professional_services: "Professional Services",
        role_sales_representative: "Sales Representative",
        role_sales_management: "Sales Management",
        role_system_presales_engineer: "System/Pre-sales Engineer",
        role_technical_support: "Technical Support",
        role_training: "Training",
        role_web_administration_master: "Web Administration/Master",
        submit_button_text: "Submit",
      },
      thank_you_page: {
        header: "Thank you.",
        thank_you_public:
          "Your application has been received and we're reviewing it now. You will receive an e-mail after it has been reviewed. We look forward to working with you!",
        thank_you_corporate:
          "You have successfully registered for the Adobe Partner Connection program.",
        return_to_partner_connection: "Return to Adobe Partner Connection",
        partner_connection_link: localizedLink("apc_link", "en_US"),
        questions_contact_helpdesk:
          "If you have any questions, you can contact the helpdesk.",
      },
      admin: {
        approval_header: "We need your approval.",
        approval_instructions:
          "The following users have requested access to Adobe Partner Connection, but we don’t recognize their e-mail address. Because you are the primary contact for this account, please approve or reject the below request(s). They will receive an e-mail notifying them of your action.",
        no_requests:
          "There are currently no requests for you to review. If there are multiple primary contacts on the account, it's possible another person already reviewed the requests.",
        name_label: "Name",
        email_label: "E-mail",
        company_label: "Company",
        approve_button: "Approve",
        reject_button: "Reject",
        request_approved: "Request Approved",
        request_rejected: "Request Rejected",
        done_instructions:
          "Once complete, you can close this window. If you need help, please contact our helpdesk.",
      },
      find_company: {
        header: "Let's find your company.",
        public_domain_company_search:
          "We can't find a company associated with the Adobe ID you’ve signed in with (%{email}). Please search for your company below.",
        company_matches:
          "We found %{count} companies matching the e-mail address you’re signed in with (%{email}).",
        company_matches_1:
          "We found %{count} company matching the e-mail address you’re signed in with (%{email}).",
        no_matches_search:
          "We couldn't find any companies matching this search term. Try to be less specific, or contact the helpdesk for assistance.",
        company_name: "Company name",
        enter_company_name: "Enter your company name",
        select_company: "Select your company",
        start_enrollment: "Start enrollment",
        company_not_listed:
          'Or, if you don\'t see your company in the list, you can <a id="apply-join" href="#">apply to join</a>.',
      },
    },
  },
  pt_BR: {
    global: {
      ims: {
        sign_in: "Fazer logon com uma Adobe ID",
        sign_out: "Fazer logoff",
      },
      header: {
        app_prefix_bold: " ",
        // app_name: 'Registro de contato do Adobe Partner Connection',
        app_name: "Adobe Partner Connection Portal",
      },
      footer: {
        region: "Região:",
        choose_region: "Escolha sua região",
        privacy: "Privacidade",
        privacy_link: localizedLink("privacy_link", "pt_BR"),
        terms_of_use: "Termos de uso",
        terms_of_use_link: localizedLink("terms_of_use_link", "pt_BR"),
        cookies: "Cookies",
        cookies_link: localizedLink("cookies_link", "pt_BR"),
        help: "Helpdesk",
        help_link: localizedLink("help_link", "pt_BR"),
      },
      errors: {
        unable_to_load: "Não é possível carregar a página",
        unauthorized: "Você não tem autorização para ver esta página",
      },
    },
    workflow: {
      progress: {
        sign_in: "Fazer logon",
        select_company: "Selecione a empresa",
        personal_information: "Informações pessoais",
        finish: "Concluir",
      },
      error_page: {
        problem_header: "Ocorreu um problema.",
        general_problem:
          "Ocorreu um problema desconhecido ao tentar concluir sua solicitação. Entre em contato com o helpdesk para que possamos ajudar você.",
        associated_other_account:
          "A Adobe ID com a qual você fez logon (%{email}) já está associada a outro programa de parceiro da Adobe: %{program}. Faça logon com outra Adobe ID ou entre em contato com o helpdesk para continuar seu registro.",
        account_not_found:
          "A Adobe ID com a qual você fez logon (%{email}) não corresponde a nenhuma conta atual do programa Adobe Partner Connection. Você pode fazer o cadastro no programa Adobe Partner Connection ou fazer logon com outra Adobe ID.",
        initial_search_problem:
          "Ocorreu um erro ao pesquisar suas informações. Atualize a página e faça a solicitação novamente ou entre em contato com o helpdesk.",
        company_search_problem:
          "Ocorreu um erro ao consultar os resultados de pesquisa da sua empresa. Tente pesquisar novamente ou entre em contato com o helpdesk.",
        admin_fetch_requests_problem:
          "Ocorreu um erro ao carregar as solicitações de acesso pendentes. Atualize a página e faça a solicitação novamente ou entre em contato com o helpdesk.",
        admin_action_problem:
          "Ocorreu um problema ao executar uma ação nesta solicitação. Tente novamente. Se os problemas persistirem, entre em contato com o helpdesk.",
        submit_request_problem:
          "Ocorreu um problema ao enviar sua solicitação. Tente enviar o formulário novamente. Se os problemas persistirem, entre em contato com o helpdesk.",
        banned_domain:
          "A Adobe ID com a qual você fez logon (%{email}) é de domínio público e não pode ser usada para o registro no programa Adobe Partner Connection. Faça logon com o endereço de email fornecido pela empresa ou entre em contato com o helpdesk para obter ajuda.",
        already_registered:
          "A Adobe ID com a qual você fez logon (%{email}) já está registrada no programa Adobe Partner Connection. Você pode fazer logon usando os links abaixo.",
        associated_non_partner_account:
          "A Adobe ID com a qual você fez logon (%{email}) já está associada a outra conta. Faça logon com outra Adobe ID ou entre em contato com o helpdesk para continuar seu registro.",
        rejected_contact:
          "A Adobe ID com a qual você fez logon (%{email}) não está autorizada a fazer o registro no programa Adobe Partner Connection no momento. Entre em contato com o helpdesk para obter ajuda.",
        partial_registration:
          "A conta à qual você está associado ainda não finalizou o processo de cadastro. Antes de continuar com o registro de contato do Partner Connection, finalize o cadastro da conta. Se enfrentar problemas durante o cadastro, entre em contato com o helpdesk para obter ajuda.",
        contact_helpdesk: "Entrar em contato com o helpdesk",
        apply_join: "Cadastre-se para participar",
        ok: "OK",
        cbc_button_text: "Consumer & Business Connection",
        cbc_link: localizedLink("cbc_link", "pt_BR"),
        apc_button_text: "Adobe Partner Connection",
        apc_link: localizedLink("apc_link", "pt_BR"),
        apc_direct_login: apcDirectLogin(),
      },
      personal_information: {
        header: "Informações pessoais",
        first_name: "Nome",
        last_name: "Sobrenome",
        email_address: "Endereço de email",
        phone_number: "Número de telefone",
        phone_number_placeholder: "Insira seu número de telefone",
        job_role: "Função",
        job_role_placeholder: "Selecione uma função",
        role_accounting: "Contabilidade",
        role_administration: "Administração",
        role_development: "Desenvolvimento",
        role_executive: "Diretoria",
        role_finance: "Finanças",
        role_helpdesk_support: "Suporte de helpdesk",
        role_it: "TI",
        role_marketing: "Marketing",
        role_owner: "Proprietário",
        role_partner_specialist: "Partner Specialist",
        role_pr_communications: "Relações públicas/comunicações",
        role_professional_services: "Serviços profissionais",
        role_sales_representative: "Representante de vendas",
        role_sales_management: "Gestão de vendas",
        role_system_presales_engineer: "Engenheiro de sistemas/pré-vendas",
        role_technical_support: "Suporte técnico",
        role_training: "Treinamento",
        role_web_administration_master: "Administração Web/Webmaster",
        submit_button_text: "Enviar",
      },
      thank_you_page: {
        header: "Obrigado.",
        thank_you_public:
          "Seu cadastro foi recebido e está sendo analisado. Você receberá um email após a conclusão da análise. Será um prazer trabalharmos juntos.",
        thank_you_corporate:
          "Você está registrado no programa Adobe Partner Connection.",
        return_to_partner_connection: "Voltar para Adobe Partner Connection",
        partner_connection_link: localizedLink("apc_link", "pt_BR"),
        questions_contact_helpdesk:
          "Caso tenha alguma dúvida, entre em contato com o helpdesk.",
      },
      admin: {
        approval_header: "Precisamos de sua aprovação.",
        approval_instructions:
          "Os seguintes usuários desejam participar de sua associação ao Adobe Partner Connection (APC). Sua associação APC dará a esses usuários acesso a conteúdo exclusivo para parceiros no portal de parceiro do APC, Adobe Consumer & Business Connection e boletins informativos por email. Como você é o contato principal desta conta, deve aprovar ou rejeitar as seguintes solicitações. Os usuários serão informados de sua decisão por email.",
        no_requests:
          "No momento, não há solicitações para análise. Se houver vários contatos principais nesta conta, outro contato pode ter analisado as solicitações.",
        name_label: "Nome",
        email_label: "Email",
        company_label: "Empresa",
        approve_button: "Aprovar",
        reject_button: "Rejeitar",
        request_approved: "Solicitação aprovada",
        request_rejected: "Solicitação rejeitada",
        done_instructions:
          "Assim que tiver finalizado, feche esta janela. Se precisar de ajuda, entre em contato com o helpdesk.",
      },
      find_company: {
        header: "Vamos encontrar sua empresa.",
        public_domain_company_search:
          "Não foi possível encontrar a empresa associada à Adobe ID com a qual você fez logon (%{email}). Pesquise sua empresa abaixo.",
        company_matches:
          "Foram encontradas %{count} empresas que correspondem ao endereço de email com o qual você fez logon (%{email}).",
        company_matches_1:
          "Foram encontradas %{count} empresas que correspondem ao endereço de email com o qual você fez logon (%{email}).",
        no_matches_search:
          "Não foi possível encontrar empresas que correspondam ao termo pesquisado. Tente ser menos específico ou entre em contato com o helpdesk para obter ajuda.",
        company_name: "Nome da empresa",
        enter_company_name: "Insira o nome de sua empresa",
        select_company: "Selecione sua empresa",
        start_enrollment: "Iniciar cadastro",
        company_not_listed:
          'Se você não localizar sua empresa na lista, <a id="apply-join" href="#">cadastre-se</a> para adicioná-la.',
      },
    },
  },
  zh_CN: {
    global: {
      ims: {
        sign_in: "使用 Adobe ID 登录",
        sign_out: "注销",
      },
      header: {
        app_prefix_bold: "Adobe",
        // app_name: 'Partner Connection 联系人注册',
        app_name: "APC门户网站",
      },
      footer: {
        region: "地区",
        choose_region: "选择地区",
        privacy: "隐私",
        privacy_link: localizedLink("privacy_link", "zh_CN"),
        terms_of_use: "使用条款",
        terms_of_use_link: localizedLink("terms_of_use_link", "zh_CN"),
        cookies: "Cookie",
        cookies_link: localizedLink("cookies_link", "zh_CN"),
        help: "帮助台",
        help_link: localizedLink("help_link", "zh_CN"),
      },
      errors: {
        unable_to_load: "无法加载此页面",
        unauthorized: "您无权查看此页面",
      },
    },
    workflow: {
      progress: {
        sign_in: "登录",
        select_company: "选择公司",
        personal_information: "个人信息",
        finish: "完成",
      },
      error_page: {
        problem_header: "出现问题。",
        general_problem:
          "尝试完成您的请求时出现未知问题。请联系帮助台，以便我们为您提供进一步帮助。",
        associated_other_account:
          "您登录时使用的 Adobe ID (%{email}) 已与其他 Adobe 合作伙伴计划关联：%{program}。您可以使用其他 Adobe ID 或联系帮助台以继续完成注册流程。",
        account_not_found:
          "您登录时使用的 Adobe ID (%{email}) 与当前任何 Adobe Partner Connection 帐户均不匹配。您可以申请加入 Adobe Partner Connection 计划或使用其他 Adobe ID 登录。",
        initial_search_problem:
          "尝试查找您的信息时出现问题。您可刷新页面再次发送请求，或联系帮助台。",
        company_search_problem:
          "查找您的公司搜索结果时出现问题。您可以再次搜索或联系帮助台。",
        admin_fetch_requests_problem:
          "尝试加载待处理的访问请求时出现问题。您可刷新页面再次发送请求，或联系帮助台。",
        admin_action_problem:
          "尝试处理此请求时出现问题。请重试。如果仍然遇到问题，请联系帮助台。",
        submit_request_problem:
          "尝试提交您的请求时出现问题。请尝试再次提交表单。如果仍然遇到问题，请联系帮助台。",
        banned_domain:
          "您登录时使用的 Adobe ID (%{email}) 属于公共域，无法用于注册加入 Adobe Partner Connection 计划。请使用您公司提供的电子邮件地址登录，或联系帮助台获取相关帮助。",
        already_registered:
          "您登录时使用的 Adobe ID (%{email}) 已注册加入 Adobe Partner Connection 计划。您可以通过以下链接登录。",
        associated_non_partner_account:
          "您登录时使用的 Adobe ID (%{email}) 已与其他帐户关联。可使用其他 Adobe ID 登录或联系帮助台以继续完成注册流程。",
        rejected_contact:
          "您登录时使用的 Adobe ID (%{email}) 暂不支持注册加入 Adobe Partner Connection 计划。请联系帮助台寻求帮助。",
        partial_registration:
          "您关联的帐户尚未完成注册流程。您继续完成 Partner Connection 联系人注册流程之前，请先完成帐户注册。如果您注册帐户时遇到问题，请联系帮助台寻求帮助。",
        contact_helpdesk: "联系帮助台",
        apply_join: "申请加入",
        ok: "确定",
        cbc_button_text: "Consumer & Business Connection",
        cbc_link: localizedLink("cbc_link", "zh_CN"),
        apc_button_text: "Adobe Partner Connection",
        apc_link: localizedLink("apc_link", "zh_CN"),
        apc_direct_login: apcDirectLogin(),
      },
      personal_information: {
        header: "个人信息",
        first_name: "名字",
        last_name: "姓氏",
        email_address: "电子邮件地址",
        phone_number: "电话号码",
        phone_number_placeholder: "输入您的电话号码",
        job_role: "工作岗位",
        job_role_placeholder: "选择工作岗位",
        role_accounting: "会计",
        role_administration: "管理",
        role_development: "开发",
        role_executive: "主管",
        role_finance: "财务",
        role_helpdesk_support: "帮助台支持",
        role_it: "IT",
        role_marketing: "市场营销",
        role_owner: "所有者",
        role_partner_specialist: "Partner Specialist",
        role_pr_communications: "公关/通信",
        role_professional_services: "专业服务",
        role_sales_representative: "销售代表",
        role_sales_management: "销售管理",
        role_system_presales_engineer: "系统/售前工程师",
        role_technical_support: "技术支持",
        role_training: "培训",
        role_web_administration_master: "网络管理员/负责人",
        submit_button_text: "提交",
      },
      thank_you_page: {
        header: "非常感谢。",
        thank_you_public:
          "我们已收到您的申请，将会立即对其进行审核。审核后，我们将通过电子邮件通知您。我们期待与您合作。",
        thank_you_corporate: "您已成功注册加入 Adobe Partner Connection 计划。",
        return_to_partner_connection: "返回 Adobe Partner Connection",
        partner_connection_link: localizedLink("apc_link", "zh_CN"),
        questions_contact_helpdesk: "如有任何问题，您可以联系帮助台。",
      },
      admin: {
        approval_header: "我们需要您的审核。",
        approval_instructions:
          "以下用户申请成为您的 Adobe Partner Connection (APC) 会员。成为您的 APC 会员后，这些用户可以通过 APC 合作伙伴门户、Adobe Consumer & Business Connection 和快讯邮件访问仅限合作伙伴访问的内容。由于您是该帐户的主要联系人，请批准或拒绝以下请求。我们会向相关用户发送电子邮件，通知他们您对其请求所执行的操作。",
        no_requests:
          "目前没有待审核的请求。如此帐户有多位主要联系人，则其他联系人可能已审核过相关请求。",
        name_label: "姓名",
        email_label: "电子邮件",
        company_label: "公司",
        approve_button: "批准",
        reject_button: "拒绝",
        request_approved: "请求已通过审核",
        request_rejected: "请求已遭拒",
        done_instructions:
          "审核完成后，您可以关闭此窗口。如果您需要帮助，请联系帮助台。",
      },
      find_company: {
        header: "搜索您的公司。",
        public_domain_company_search:
          "我们找不到与您登录时使用的 Adobe ID (%{email}) 关联的公司。请在下方搜索您的公司。",
        company_matches:
          "我们找到 %{count} 家与您登录时使用的电子邮件地址匹配的公司。(%{email})",
        company_matches_1:
          "我们找到 %{count} 家与您登录时使用的电子邮件地址匹配的公司。(%{email})",
        no_matches_search:
          "我们找不到任何与该搜索关键字匹配的公司。您可以尝试扩大搜索范围，或联系帮助台寻求帮助。",
        company_name: "公司名称",
        enter_company_name: "输入您的公司名称",
        select_company: "选择您的公司",
        start_enrollment: "开始注册",
        company_not_listed:
          '或者，如果在列表中未找到您的公司，则您可以<a id="apply-join" href="#">申请</a>添加公司。',
      },
    },
  },
  fr_FR: {
    global: {
      ims: {
        sign_in: "Connexion avec un Adobe ID",
        sign_out: "Déconnexion",
      },
      header: {
        app_prefix_bold: " ",
        // app_name: 'Inscription du contact Adobe Partner Connection',
        app_name: "Adobe Partner Connection Portal",
      },
      footer: {
        region: "Région:",
        choose_region: "Choisissez votre région",
        privacy: "Confidentialité",
        privacy_link: localizedLink("privacy_link", "fr_FR"),
        terms_of_use: "Conditions d'utilisation",
        terms_of_use_link: localizedLink("terms_of_use_link", "fr_FR"),
        cookies: "Cookies",
        cookies_link: localizedLink("cookies_link", "fr_FR"),
        help: "Service d'assistance",
        help_link: localizedLink("help_link", "fr_FR"),
      },
      errors: {
        unable_to_load: "Impossible de charger cette page",
        unauthorized: "Vous n’êtes pas autorisé à afficher cette page",
      },
    },
    workflow: {
      progress: {
        sign_in: "Se connecter",
        select_company: "Sélectionner l'entreprise",
        personal_information: "Informations personnelles",
        finish: "Terminer",
      },
      error_page: {
        problem_header: "Une erreur est survenue.",
        general_problem:
          "Un problème inconnu s'est produit lors de la finalisation de votre demande. Veuillez contacter notre assistance.",
        associated_other_account:
          "L'Adobe ID que vous avez saisi (%{email}) est déjà associé à un autre programme partenaire Adobe: %{program}. Connectez-vous avec un autre Adobe ID ou contactez notre assistance.",
        account_not_found:
          "L'Adobe ID que vous avez saisi (%{email}) ne correspond à aucun compte Adobe Partner Connection. Demandez à adhérer au programme Adobe Partner Connection ou connectez-vous avec un autre Adobe ID.",
        initial_search_problem:
          "Un problème est survenu lors de la recherche de vos informations. Actualisez la page pour soumettre à nouveau votre demande ou contactez l'assistance.",
        company_search_problem:
          "Un problème s'est produit lors de la recherche de votre entreprise. Relancez la recherche ou contactez l'assistance.",
        admin_fetch_requests_problem:
          "Une erreur s'est produite lors du chargement des demandes d'accès. Actualisez la page pour soumettre à nouveau votre demande ou contactez l'assistance.",
        admin_action_problem:
          "Un problème s'est produit lors du traitement de cette demande. Veuillez réessayer. Si des problèmes persistent, contactez l'assistance.",
        submit_request_problem:
          "Un problème est survenu lors de la soumission de votre demande. Merci de soumettre une nouvelle fois le formulaire. Si des problèmes persistent, contactez l'assistance.",
        banned_domain:
          "L'Adobe ID que vous avez saisi (%{email}) est associé à un nom de domaine public qui ne vous permet pas d’adhérer au programme Adobe Partner Connection. Utilisez une adresse électronique fournie par votre entreprise ou contactez l'assistance.",
        already_registered:
          "L'Adobe ID que vous avez saisi (%{email}) est déjà utilisé pour le programme Adobe Partner Connection. Vous pouvez vous connecter via les liens ci-dessous.",
        associated_non_partner_account:
          "L'Adobe ID que vous avez saisi (%{email}) est déjà associé à un autre compte. Utilisez un autre Adobe ID ou contactez notre assistance pour poursuivre votre adhésion.",
        rejected_contact:
          "Pour l'instant, l'adhésion au programme Adobe Partner Connection avec l'Adobe ID que vous avez utilisé (%{email}) n’est pas autorisée. Veuillez contacter l'assistance.",
        partial_registration:
          "L'inscription du compte auquel vous êtes associé n'a pas été finalisée. Avant de continuer, merci de finaliser l’inscription. Si vous rencontrez des problèmes, nous vous invitons à contacter notre assistance.",
        contact_helpdesk: "Contacter l'assistance",
        apply_join: "Demander à adhérer",
        ok: "OK",
        cbc_button_text: "Consumer & Business Connection",
        cbc_link: localizedLink("cbc_link", "fr_FR"),
        apc_button_text: "Adobe Partner Connection",
        apc_link: localizedLink("apc_link", "fr_FR"),
        apc_direct_login: apcDirectLogin(),
      },
      personal_information: {
        header: "Informations personnelles",
        first_name: "Prénom",
        last_name: "Nom de famille",
        email_address: "Adresse électronique",
        phone_number: "Numéro de téléphone",
        phone_number_placeholder: "Saisissez votre numéro de téléphone",
        job_role: "Fonction",
        job_role_placeholder: "Sélectionnez une fonction",
        role_accounting: "Comptabilité",
        role_administration: "Administration",
        role_development: "Développement",
        role_executive: "Cadre",
        role_finance: "Finance",
        role_helpdesk_support: "Service d'assistance",
        role_it: "IT",
        role_marketing: "Marketing",
        role_owner: "Propriétaire",
        role_partner_specialist: "Partner Specialist",
        role_pr_communications: "Relations publiques/communication",
        role_professional_services: "Services professionnels",
        role_sales_representative: "Commercial",
        role_sales_management: "Direction des ventes",
        role_system_presales_engineer: "Ingénieur système/avant-vente",
        role_technical_support: "Support technique",
        role_training: "Formation",
        role_web_administration_master: "Administration web/webmestre",
        submit_button_text: "Envoyer",
      },
      thank_you_page: {
        header: "Merci.",
        thank_you_public:
          "Votre demande est en cours de traitement. Vous recevrez un e-mail lorsqu'elle aura été traitée. À très bientôt.",
        thank_you_corporate:
          "Vous avez adhéré au programme Adobe Partner Connection.",
        return_to_partner_connection:
          "Revenir au portail Adobe Partner Connection",
        partner_connection_link: localizedLink("apc_link", "fr_FR"),
        questions_contact_helpdesk:
          "Pour toute question, veuillez contacter l'assistance.",
      },
      admin: {
        approval_header: "Nous avons besoin de votre accord.",
        approval_instructions:
          "Les utilisateurs suivants ont demandé à être liés à votre adhésion à Adobe Partner Connection (APC). Si vous acceptez leur demande, ils auront accès au contenu réservé aux partenaires sur le portail APC, à Adobe Consumer & Business Connection et aux newsletters électroniques. Étant donné que vous êtes le contact principal de ce compte, nous vous invitons à accepter ou refuser les demandes suivantes. Les utilisateurs concernés seront informés de votre décision par e-mail.",
        no_requests:
          "Il n’y a pour l’instant aucune demande à valider. S’il existe plusieurs contacts principaux pour ce compte, il se peut que les demandes aient été traitées par une autre personne.",
        name_label: "Nom",
        email_label: "E-mail",
        company_label: "Entreprise",
        approve_button: "Accepter",
        reject_button: "Refuser",
        request_approved: "Demande acceptée",
        request_rejected: "Demande refusée",
        done_instructions:
          "Fermez la fenêtre lorsque vous avez terminé. N'hésitez pas à contacter notre assistance pour toute question.",
      },
      find_company: {
        header: "À présent, recherchons votre entreprise.",
        public_domain_company_search:
          "Nous ne trouvons aucune entreprise associée à l'Adobe ID que vous avez utilisé (%{email}). Veuillez rechercher votre entreprise dans la liste ci-dessous.",
        company_matches:
          "Nous avons trouvé %{count} entreprises correspondant à l'adresse e-mail que vous avez utilisée pour vous connecter (%{email}).",
        company_matches_1:
          "Nous avons trouvé %{count} entreprises correspondant à l'adresse e-mail que vous avez utilisée pour vous connecter (%{email}).",
        no_matches_search:
          "Aucune entreprise ne correspond à votre recherche. Le terme que vous avez saisi est peut-être trop spécifique. Modifiez votre saisie ou contactez notre assistance.",
        company_name: "Nom de la société",
        enter_company_name: "Saisissez le nom de votre entreprise",
        select_company: "Sélectionnez votre entreprise",
        start_enrollment: "Démarrer l'inscription",
        company_not_listed:
          'Ou, si vous ne trouvez pas votre entreprise dans la liste, vous pouvez <a id="apply-join" href="#">demander</a> à la faire ajouter.',
      },
    },
  },
  de_DE: {
    global: {
      ims: {
        sign_in: "Mit Adobe ID anmelden",
        sign_out: "Abmelden",
      },
      header: {
        app_prefix_bold: "Adobe",
        // app_name: 'Partner Connection: Registrierung',
        app_name: "Adobe Partner Connection Portal",
      },
      footer: {
        region: "Region:",
        choose_region: "Region wählen",
        privacy: "Datenschutz",
        privacy_link: localizedLink("privacy_link", "de_DE"),
        terms_of_use: "Nutzungsbedingungen",
        terms_of_use_link: localizedLink("terms_of_use_link", "de_DE"),
        cookies: "Cookies",
        cookies_link: localizedLink("cookies_link", "de_DE"),
        help: "Helpdesk",
        help_link: localizedLink("help_link", "de_DE"),
      },
      errors: {
        unable_to_load: "Seite kann nicht geladen werden",
        unauthorized: "Sie sind nicht berechtigt, diese Seite anzuzeigen.",
      },
    },
    workflow: {
      progress: {
        sign_in: "Anmelden",
        select_company: "Unternehmen auswählen",
        personal_information: "Angaben zur Person",
        finish: "Beenden",
      },
      error_page: {
        problem_header: "Ein Problem ist aufgetreten.",
        general_problem:
          "Bei der Bearbeitung deiner Anfrage ist ein unbekanntes Problem aufgetreten. Bitte kontaktiere den Helpdesk, um weitere Unterstützung zu erhalten.",
        associated_other_account:
          "Die Adobe ID, mit der du dich angemeldet hast (%{email}), wird bereits für ein anderes Partnerprogramm von Adobe verwendet: %{program}. Melde dich mit einer anderen Adobe ID an, oder kontaktiere den Helpdesk, um mit deiner Registrierung fortzufahren.",
        account_not_found:
          "Die Adobe ID, mit der du dich angemeldet hast (%{email}), ist keinem aktuellen Adobe Partner Connection-Konto zugeordnet. Du kannst die Teilnahme am Adobe Partner Connection-Programm beantragen oder dich mit einer anderen Adobe ID anmelden.",
        initial_search_problem:
          "Bei der Ermittlung deiner Daten ist ein Fehler aufgetreten. Aktualisiere die Seite, um die Anfrage erneut zu übermitteln, oder wende dich an den Helpdesk.",
        company_search_problem:
          "Bei der Ermittlung der Suchergebnisse für dein Unternehmen ist ein Fehler aufgetreten. Führe die Suche erneut durch, oder wende dich an den Helpdesk.",
        admin_fetch_requests_problem:
          "Beim Versuch, die ausstehenden Zugriffsanfragen zu laden, ist ein Problem aufgetreten. Aktualisiere die Seite, um die Anfrage erneut zu übermitteln, oder wende dich an den Helpdesk.",
        admin_action_problem:
          "Beim Versuch, auf diese Anfrage zu reagieren, ist ein Problem aufgetreten. Bitte versuche es noch einmal. Falls weiterhin Probleme auftreten, wende dich an den Helpdesk.",
        submit_request_problem:
          "Beim Versuch, deine Anfrage zu übermitteln, ist ein Problem aufgetreten. Bitte versuche, das Formular erneut zu übermitteln. Falls weiterhin Probleme auftreten, wende dich an den Helpdesk.",
        banned_domain:
          "Die Adobe ID, mit der du angemeldet bist (%{email}), basiert auf einer Public Domain, die nicht zur Registrierung für das Adobe Partner Connection-Programm verwendet werden kann. Bitte melde dich mit der von deinem Unternehmen bereitgestellten E-Mail-Adresse an, oder wende dich an den Helpdesk.",
        already_registered:
          "Die Adobe ID, mit der du dich angemeldet hast (%{email}), ist bereits für das Adobe Partner Connection-Programm registriert. Du kannst dich über die nachfolgenden Links anmelden.",
        associated_non_partner_account:
          "Die Adobe ID, mit der du dich angemeldet hast (%{email}), wird bereits für ein anderes Konto verwendet. Melde dich mit einer anderen Adobe ID an, oder kontaktiere den Helpdesk, um mit der Registrierung fortfahren zu können.",
        rejected_contact:
          "Die Adobe ID, mit der du dich angemeldet hast (%{email}), kann derzeit nicht zur Registrierung für das Adobe Partner Connection-Programm verwendet werden. Bitte wende dich an den Helpdesk, um Unterstützung zu erhalten.",
        partial_registration:
          "Für das dir zugeordnete Konto wurde der Registrierungsprozess noch nicht abgeschlossen. Bevor du mit der Registrierung deiner Kontaktdaten für das Partner Connection-Programm fortfahren kannst, schließe zunächst die Registrierung für das Konto ab. Falls Probleme bei der Registrierung auftreten, wende dich an den Helpdesk.",
        contact_helpdesk: "Helpdesk kontaktieren",
        apply_join: "Teilnahme beantragen",
        ok: "OK",
        cbc_button_text: "Consumer & Business Connection",
        cbc_link: localizedLink("cbc_link", "de_DE"),
        apc_button_text: "Adobe Partner Connection",
        apc_link: localizedLink("apc_link", "de_DE"),
        apc_direct_login: apcDirectLogin(),
      },
      personal_information: {
        header: "Angaben zur Person",
        first_name: "Vorname",
        last_name: "Nachname",
        email_address: "E-Mail-Adresse",
        phone_number: "Telefonnummer",
        phone_number_placeholder: "Telefonnummer eingeben",
        job_role: "Position",
        job_role_placeholder: "Position auswählen",
        role_accounting: "Buchhaltung",
        role_administration: "Verwaltung",
        role_development: "Entwicklung",
        role_executive: "Geschäftsführung",
        role_finance: "Finanzwesen",
        role_helpdesk_support: "Helpdesk/Support",
        role_it: "IT",
        role_marketing: "Marketing",
        role_owner: "Eigentümer/Inhaber",
        role_partner_specialist: "Partner Specialist",
        role_pr_communications: "PR/Kommunikation",
        role_professional_services: "Professionelle Dienstleistungen",
        role_sales_representative: "Vertriebsmitarbeiter",
        role_sales_management: "Vertriebsleitung",
        role_system_presales_engineer: "System-/Pre-Sales-Ingenieur",
        role_technical_support: "Technischer Support",
        role_training: "Schulung",
        role_web_administration_master: "Web-Administrator/-master",
        submit_button_text: "Abschicken",
      },
      thank_you_page: {
        header: "Vielen Dank!",
        thank_you_public:
          "Wir haben deinen Antrag erhalten und werden ihn nun überprüfen. Sobald die Überprüfung abgeschlossen ist, erhältst du eine E-Mail. Wir freuen uns darauf, mit dir zusammenzuarbeiten.",
        thank_you_corporate:
          "Du bist nun für das Adobe Partner Connection-Programm registriert.",
        return_to_partner_connection: "Zurück zu Adobe Partner Connection",
        partner_connection_link: localizedLink("apc_link", "de_DE"),
        questions_contact_helpdesk:
          "Falls du Fragen hast, wende dich an den Helpdesk.",
      },
      admin: {
        approval_header: "Wir benötigen deine Genehmigung.",
        approval_instructions:
          "Die folgenden Anwender haben beantragt, zu deinem Adobe Partner Connection-Konto (APC) hinzugefügt zu werden. Anwender, die über dein Konto an der APC teilnehmen, erhalten Zugriff auf Partner-Inhalte im APC-Portal, Adobe Consumer and Business Connection und E-Mail-Newsletter. Da du der Hauptansprechpartner für dieses Konto bist, kannst du die folgende(n) Anfrage(n) genehmigen oder ablehnen. Die Anwender werden per E-Mail über deine Aktion benachrichtigt.",
        no_requests:
          "Momentan liegen keine Anfragen zur Überprüfung vor. Falls mehrere Hauptansprechpartner für dieses Konto festgelegt wurden, kann es sein, dass ein anderer Ansprechpartner die Anfrage(n) bereits überprüft hat.",
        name_label: "Name",
        email_label: "E-mail",
        company_label: "Unternehmen",
        approve_button: "Genehmigen",
        reject_button: "Ablehnen",
        request_approved: "Anfrage genehmigt",
        request_rejected: "Anfrage abgelehnt",
        done_instructions:
          "Sobald du den Vorgang abgeschlossen hast, kannst du dieses Fenster schließen. Falls du Unterstützung benötigst, wende dich an den Helpdesk.",
      },
      find_company: {
        header: "Suche nach deinem Unternehmen.",
        public_domain_company_search:
          "Wir konnten kein Unternehmen finden, das der Adobe ID zugeordnet ist, die du zur Anmeldung verwendet hast (%{email}). Bitte suche unten nach deinem Unternehmen.",
        company_matches:
          "Wir haben %{count} Unternehmen gefunden, die der E-Mail-Adresse entsprechen, die du zur Anmeldung verwendet hast (%{email}).",
        company_matches_1:
          "Wir haben %{count} Unternehmen gefunden, die der E-Mail-Adresse entsprechen, die du zur Anmeldung verwendet hast (%{email}).",
        no_matches_search:
          "Wir konnten kein Unternehmen finden, das diesem Suchbegriff entspricht. Verwende einen allgemeineren Suchbegriff, oder kontaktiere den Helpdesk.",
        company_name: "Name des Unternehmens",
        enter_company_name: "Name des Unternehmens eingeben",
        select_company: "Dein Unternehmen auswählen",
        start_enrollment: "Registrierung starten",
        company_not_listed:
          'Falls dein Unternehmen nicht in der Liste angezeigt wird, kannst du die Aufnahme deines Unternehmens <a id="apply-join" href="#">beantragen</a>.',
      },
    },
  },
  it_IT: {
    global: {
      ims: {
        sign_in: "Accedi con Adobe ID",
        sign_out: "Esci",
      },
      header: {
        app_prefix_bold: " ",
        // app_name: 'Registrazione contatti Adobe Partner Connection',
        app_name: "Adobe Partner Connection Portal",
      },
      footer: {
        region: "Area geografica:",
        choose_region: "Scegli il paese",
        privacy: "Privacy",
        privacy_link: localizedLink("privacy_link", "it_IT"),
        terms_of_use: "Condizioni di utilizzo",
        terms_of_use_link: localizedLink("terms_of_use_link", "it_IT"),
        cookies: "Cookie",
        cookies_link: localizedLink("cookies_link", "it_IT"),
        help: "Help desk",
        help_link: localizedLink("help_link", "it_IT"),
      },
      errors: {
        unable_to_load: "Impossibile caricare la pagina",
        unauthorized:
          "Non disponi delle autorizzazioni per visualizzare questa pagina",
      },
    },
    workflow: {
      progress: {
        sign_in: "Registrati",
        select_company: "Seleziona l’azienda",
        personal_information: "Informazioni personali",
        finish: "Fine",
      },
      error_page: {
        problem_header: "Si è verificato un problema.",
        general_problem:
          "A causa di un problema sconosciuto non è stato possibile completare la tua richiesta. Contatta l'help desk per ricevere ulteriore assistenza.",
        associated_other_account:
          "L’Adobe ID con cui hai effettuato l'accesso (%{email}) è già associato a un altro programma per i partner Adobe: %{program}. Per continuare la registrazione, prova ad accedere con un altro Adobe ID oppure contatta l'help desk.",
        account_not_found:
          "L’Adobe ID con cui hai effettuato l'accesso (%{email}) non corrisponde ad alcun account corrente di Adobe Partner Connection. Puoi richiedere di aderire al programma Adobe Partner Connection o accedere con un altro Adobe ID.",
        initial_search_problem:
          "Si è verificato un problema durante la ricerca delle tue informazioni. Per provare di nuovo a inviare la richiesta, aggiorna la pagina oppure contatta l'help desk.",
        company_search_problem:
          "Si è verificato un problema durante la visualizzazione dei risultati di ricerca relativi alla tua azienda. Prova a effettuare una nuova ricerca oppure contatta l'help desk.",
        admin_fetch_requests_problem:
          "Si è verificato un problema durante il caricamento delle richieste di accesso in sospeso. Per provare di nuovo a inviare la richiesta, aggiorna la pagina oppure contatta l'help desk.",
        admin_action_problem:
          "Si è verificato un problema durante l’elaborazione della richiesta. Riprova. Se continui a riscontrare problemi, contatta l'help desk.",
        submit_request_problem:
          "Si è verificato un problema durante l’invio della richiesta. Prova a inviare nuovamente il modulo. Se continui a riscontrare problemi, contatta l'help desk.",
        banned_domain:
          "L’Adobe ID con cui hai effettuato l’accesso (%{email}) è un dominio pubblico che non può essere utilizzato per la registrazione al programma Adobe Partner Connection. Registrati con l'indirizzo e-mail fornito dall'azienda oppure contatta l'help desk per ricevere assistenza.",
        already_registered:
          "L'Adobe ID con cui hai effettuato l'accesso (%{email}) è già registrato per il programma Adobe Partner Connection. Puoi effettuare l'accesso tramite i link riportati di seguito.",
        associated_non_partner_account:
          "L’Adobe ID con cui hai effettuato l'accesso (%{email}) è già associato a un altro account. Per continuare la registrazione, accedi con un altro Adobe ID oppure contatta l'help desk.",
        rejected_contact:
          "Al momento l'Adobe ID con cui hai effettuato l'accesso (%{email}) non è autorizzato per la registrazione al programma Adobe Partner Connection. Contatta l'help desk per ricevere assistenza.",
        partial_registration:
          "La procedura di iscrizione dell'account a te associato non è ancora stata completata. Per poter continuare con la registrazione dei contatti Partner Connection, devi prima completare l'iscrizione dell'account. In caso di problemi con l'iscrizione, contatta l'help desk per ricevere assistenza.",
        contact_helpdesk: "Contatta l'help desk",
        apply_join: "Richiedi di aderire",
        ok: "OK",
        cbc_button_text: "Consumer & Business Connection",
        cbc_link: localizedLink("cbc_link", "it_IT"),
        apc_button_text: "Adobe Partner Connection",
        apc_link: localizedLink("apc_link", "it_IT"),
        apc_direct_login: apcDirectLogin(),
      },
      personal_information: {
        header: "Informazioni personali",
        first_name: "Nome",
        last_name: "Cognome",
        email_address: "Indirizzo e-mail",
        phone_number: "Telefono",
        phone_number_placeholder: "Inserisci il tuo recapito telefonico",
        job_role: "Posizione",
        job_role_placeholder: "Seleziona una posizione",
        role_accounting: "Contabilità",
        role_administration: "Amministrazione",
        role_development: "Sviluppo",
        role_executive: "Dirigente",
        role_finance: "Finanza",
        role_helpdesk_support: "Assistenza help desk",
        role_it: "IT",
        role_marketing: "Marketing",
        role_owner: "Titolare",
        role_partner_specialist: "Partner Specialist",
        role_pr_communications: "PR/Comunicazioni",
        role_professional_services: "Servizi professionali",
        role_sales_representative: "Responsabile vendite",
        role_sales_management: "Gestione vendite",
        role_system_presales_engineer: "Sistemista/Tecnico pre-vendita",
        role_technical_support: "Supporto tecnico",
        role_training: "Formazione",
        role_web_administration_master: "Amministratore web/Webmaster",
        submit_button_text: "Inoltra",
      },
      thank_you_page: {
        header: "Grazie.",
        thank_you_public:
          "Abbiamo ricevuto la tua domanda e la stiamo esaminando ora. Non appena avremo finito, ti invieremo un'e-mail. Non vediamo l'ora di lavorare con te.",
        thank_you_corporate:
          "La tua registrazione al programma Adobe Partner Connection è stata completata correttamente.",
        return_to_partner_connection: "Torna ad Adobe Partner Connection",
        partner_connection_link: localizedLink("apc_link", "it_IT"),
        questions_contact_helpdesk:
          "Per qualsiasi domanda puoi contattare l'help desk.",
      },
      admin: {
        approval_header: "Abbiamo bisogno della tua approvazione.",
        approval_instructions:
          "I seguenti utenti hanno richiesto di aderire alla tua iscrizione ad Adobe Partner Connection (APC). Con l'iscrizione ad APC questi utenti potranno accedere ai contenuti riservati ai partner tramite il portale APC, Adobe Consumer & Business Connection e le newsletter inviate via e-mail. Dato che sei il contatto principale per questo account, ti chiediamo di approvare o rifiutare le seguenti richieste. Le persone interessate riceveranno un’e-mail di notifica della tua decisione.",
        no_requests:
          "Al momento non ci sono richieste da rivedere. Nel caso ci fossero più contatti principali assegnati a questo account, un altro incaricato potrebbe aver già esaminato le richieste.",
        name_label: "Nome",
        email_label: "E-mail",
        company_label: "Società",
        approve_button: "Approva",
        reject_button: "Rifiuta",
        request_approved: "Richiesta approvata",
        request_rejected: "Richiesta rifiutata",
        done_instructions:
          "Al termine puoi chiedere questa finestra. Se hai bisogno di assistenza, contatta l’help desk.",
      },
      find_company: {
        header: "Troviamo la tua azienda.",
        public_domain_company_search:
          "Non riusciamo a trovare un'azienda associata all’Adobe ID con cui hai effettuato l'accesso (%{email}). Cerca la tua azienda qui sotto.",
        company_matches:
          "Abbiamo trovato %{count} aziende che corrispondono all'indirizzo e-mail con cui hai effettuato l'accesso.",
        company_matches_1:
          "Abbiamo trovato %{count} aziende che corrispondono all'indirizzo e-mail con cui hai effettuato l'accesso.",
        no_matches_search:
          "Non siamo riusciti a trovare nessuna azienda corrispondente a questo termine di ricerca. Riprova con un termine di ricerca meno specifico oppure contatta l'help desk per assistenza.",
        company_name: "Nome azienda",
        enter_company_name: "Inserisci il nome della tua azienda",
        select_company: "Seleziona la tua azienda",
        start_enrollment: "Avvia l'iscrizione",
        company_not_listed:
          'In alternativa, se non vedi la tua azienda nell\'elenco, puoi <a id="apply-join" href="#">chiedere</a> di aggiungerla.',
      },
    },
  },
  ja_JP: {
    global: {
      ims: {
        sign_in: "Adobe IDでログイン",
        sign_out: "ログアウト",
      },
      header: {
        app_prefix_bold: "Adobe",
        // app_name: 'Partner Connection連絡先の登録',
        app_name: "Adobe Partner Connection Portal",
      },
      footer: {
        region: "地域",
        choose_region: "地域を選択",
        privacy: "プライバシー",
        privacy_link: localizedLink("privacy_link", "ja_JP"),
        terms_of_use: "利用条件",
        terms_of_use_link: localizedLink("terms_of_use_link", "ja_JP"),
        cookies: "Cookie",
        cookies_link: localizedLink("cookies_link", "ja_JP"),
        help: "ヘルプデスク（連絡先は下記URLの下部にあります）",
        help_link: localizedLink("help_link", "ja_JP"),
      },
      errors: {
        unable_to_load: "ページを読み込めません",
        unauthorized: "このページを表示する権限がありません",
      },
    },
    workflow: {
      progress: {
        sign_in: "ログインする",
        select_company: "会社を選択",
        personal_information: "個人情報",
        finish: "完了",
      },
      error_page: {
        problem_header: "問題が発生しました。",
        general_problem:
          "お客様のリクエストの処理中に未知の問題が発生しました。より詳しく問題に対応するため、ヘルプデスクにご連絡ください。",
        associated_other_account:
          "(%{email})でログインしたAdobe IDは、既に別のアドビパートナープログラム%{program}と関連付けられています。別のAdobe IDでログインするか、ヘルプデスクに連絡して登録を続けることができます。",
        account_not_found:
          "(%{email})でログインしたAdobe IDは、現在のAdobe Partner Connectionアカウントのいずれとも一致しません。Adobe Partner Connectionプログラムへの参加を申請するか、別のAdobe IDでログインできます。",
        initial_search_problem:
          "情報の検索時に問題が発生しました。ページを更新してリクエストを再試行するか、ヘルプデスクにご連絡ください。",
        company_search_problem:
          "お客様の会社の検索結果を検索する際に問題が発生しました。再度検索をおこなうか、ヘルプデスクにご連絡ください。",
        admin_fetch_requests_problem:
          "保留中のアクセスリクエストの読み込み時に問題が発生しました。ページを更新してリクエストを再試行するか、ヘルプデスクにご連絡ください。",
        admin_action_problem:
          "このリクエストの処理時に問題が発生しました。もう一度お試しください。問題が解消されない場合は、ヘルプデスクにご連絡ください。",
        submit_request_problem:
          "リクエストの送信時に問題が発生しました。フォームを再送信してください。問題が解消されない場合は、ヘルプデスクにご連絡ください。",
        banned_domain:
          "(%{email})でログインしたAdobe IDは、Adobe Partner Connectionプログラムの登録に使用できないパブリックドメインです。会社提供の電子メールアドレスでログインするか、ヘルプデスクにサポートを依頼してください。",
        already_registered:
          "(%{email})でログインしたAdobe IDは、既にAdobe Partner Connectionプログラムに登録されています。以下のリンクからログインできます。",
        associated_non_partner_account:
          "(%{email})でログインしたAdobe IDは、既に別のアカウントと関連付けられています。別のAdobe IDでログインするか、ヘルプデスクに連絡して登録を続けてください。",
        rejected_contact:
          "(%{email})でログインしたAdobe IDは、現時点でAdobe Partner Connectionプログラムへの登録が許可されません。ヘルプデスクにサポートを依頼してください。",
        partial_registration:
          "関連付けたアカウントは、まだ登録プロセスが完了していません。Adobe Partner Connection連絡先の登録を続けるには、アカウント登録を完了する必要があります。登録に問題がある場合は、ヘルプデスクにサポートを依頼してください。",
        contact_helpdesk: "ヘルプデスクに連絡",
        apply_join: "参加を申請",
        ok: "OK",
        cbc_button_text: "Consumer & Business Connection",
        cbc_link: localizedLink("cbc_link", "ja_JP"),
        apc_button_text: "Adobe Partner Connection",
        apc_link: localizedLink("apc_link", "ja_JP"),
        apc_direct_login: apcDirectLogin(),
      },
      personal_information: {
        header: "個人情報",
        first_name: "名",
        last_name: "姓",
        email_address: "電子メールアドレス",
        phone_number: "電話番号",
        phone_number_placeholder: "電話番号を入力",
        job_role: "役割",
        job_role_placeholder: "役割を選択",
        role_accounting: "会計",
        role_administration: "管理",
        role_development: "開発",
        role_executive: "経営者",
        role_finance: "財務",
        role_helpdesk_support: "ヘルプデスクサポート",
        role_it: "IT",
        role_marketing: "マーケティング",
        role_owner: "オーナー",
        role_partner_specialist: "Partner Specialist",
        role_pr_communications: "広報",
        role_professional_services: "プロフェッショナルサービス",
        role_sales_representative: "営業担当者",
        role_sales_management: "販売管理",
        role_system_presales_engineer:
          "システムエンジニア／プリセールスエンジニア",
        role_technical_support: "テクニカルサポート",
        role_training: "トレーニング",
        role_web_administration_master: "web管理／webマスター",
        submit_button_text: "送信",
      },
      thank_you_page: {
        header: "ありがとうございます。",
        thank_you_public:
          "申請を受け取りました。内容を確認中です。確認が終わりましたら電子メールで通知いたします。ご利用をお待ちしております。",
        thank_you_corporate:
          "Adobe Partner Connectionプログラムへの登録が完了しました。",
        return_to_partner_connection: "Adobe Partner Connectionに戻る",
        partner_connection_link: localizedLink("apc_link", "ja_JP"),
        questions_contact_helpdesk:
          "ご不明な点などございましたら、ヘルプデスクにご連絡ください。",
      },
      admin: {
        approval_header: "お客様の承認が必要です。",
        approval_instructions:
          "次のユーザーがお客様のAdobe Partner Connection（APC）メンバーシップへの参加を申請しました。APCメンバーシップに参加すると、APCパートナーポータル、Adobe Consumer & Business Connection、および電子メールニュースレターのパートナー専用コンテンツにアクセスできるようになります。お客様がこのアカウントの連絡窓口であるため、次のリクエストを承認または拒否してください。ユーザーには電子メールで結果が通知されます。",
        no_requests:
          "現在、確認対象のリクエストはありません。このアカウントに複数の連絡窓口がある場合は、他の方が既にこのリクエストを確認済みの場合があります。",
        name_label: "名前",
        email_label: "電子メールアドレス",
        company_label: "会社",
        approve_button: "承認",
        reject_button: "拒否",
        request_approved: "リクエストを承認",
        request_rejected: "リクエストを拒否",
        done_instructions:
          "完了したら、ウィンドウを閉じてください。サポートが必要な場合は、ヘルプデスクにご連絡ください。",
      },
      find_company: {
        header: "お客様の会社を検索してみましょう。",
        public_domain_company_search:
          "(%{email})でログインしたAdobe IDに関連付けられた会社が見つかりません。以下でお客様の会社を検索してください。",
        company_matches:
          "ログインで使用した電子メールアドレスに一致する会社が%{count}社見つかりました。(%{email})",
        company_matches_1:
          "ログインで使用した電子メールアドレスに一致する会社が%{count}社見つかりました。 (%{email})",
        no_matches_search:
          "この検索条件に一致する会社は見つかりませんでした。範囲を広げて検索するか、ヘルプデスクにサポートを依頼してください。",
        company_name: "会社名",
        enter_company_name: "会社名を入力",
        select_company: "会社を選択",
        start_enrollment: "登録を開始",
        company_not_listed:
          'また、一覧に会社がない場合は、会社の追加を<a id="apply-join" href="#">申請</a>できます。',
      },
    },
  },
  ko_KR: {
    global: {
      ims: {
        sign_in: "Adobe ID로 로그인",
        sign_out: "로그아웃",
      },
      header: {
        app_prefix_bold: "Adobe",
        // app_name: 'Partner Connection 연락처 등록',
        app_name: "Adobe Partner Connection Portal",
      },
      footer: {
        region: "지역",
        choose_region: "지역 선택",
        privacy: "개인 정보 보호",
        privacy_link: localizedLink("privacy_link", "ko_KR"),
        terms_of_use: "사용 약관",
        terms_of_use_link: localizedLink("terms_of_use_link", "ko_KR"),
        cookies: "쿠키",
        cookies_link: localizedLink("cookies_link", "ko_KR"),
        help: "헬프데스크",
        help_link: localizedLink("help_link", "ko_KR"),
      },
      errors: {
        unable_to_load: "페이지를 로드할 수 없음",
        unauthorized: "이 페이지를 볼 권한이 없습니다.",
      },
    },
    workflow: {
      progress: {
        sign_in: "로그인",
        select_company: "회사 선택",
        personal_information: "개인 정보",
        finish: "마침",
      },
      error_page: {
        problem_header: "문제가 발생했습니다.",
        general_problem:
          "요청을 완료하는 동안 알 수 없는 문제가 발생했습니다. 헬프데스크에 문의하시면 도와드리겠습니다.",
        associated_other_account:
          "(%{email})(으)로 로그인한 Adobe ID가 이미 다른 Adobe 파트너 프로그램, %{program}과(와) 연결되어 있습니다. 다른 Adobe ID로 로그인하거나 헬프데스크에 문의하여 등록을 계속할 수 있습니다.",
        account_not_found:
          "(%{email})(으)로 로그인한 Adobe ID가 현재 Adobe Partner Connection 계정과 일치하지 않습니다. Adobe Partner Connection 프로그램 등록을 신청하거나 다른 Adobe ID로 로그인할 수 있습니다.",
        initial_search_problem:
          "정보를 조회하는 동안 문제가 발생했습니다. 페이지를 새로 고침하여 다시 요청을 시도하거나 헬프데스크에 문의할 수 있습니다.",
        company_search_problem:
          "회사 검색 결과를 조회하는 동안 문제가 발생했습니다. 다시 검색을 시도하거나 헬프데스크에 문의할 수 있습니다.",
        admin_fetch_requests_problem:
          "보류 중인 액세스 요청을 로드하는 동안 문제가 발생했습니다. 페이지를 새로 고침하여 다시 요청을 시도하거나 헬프데스크에 문의할 수 있습니다.",
        admin_action_problem:
          "이 요청에 조취를 취하는 동안 문제가 발생했습니다. 다시 시도하십시오. 문제가 계속 발생하는 경우 헬프데스크에 문의하십시오.",
        submit_request_problem:
          "요청을 제출하는 동안 문제가 발생했습니다. 다시 양식을 제출해 주십시오. 문제가 계속 발생하는 경우 헬프데스크에 문의하십시오.",
        banned_domain:
          "(%{email})(으)로 로그인한 Adobe ID가 Adobe Partner Connection 프로그램 등록에 사용할 수 없는 공용 도메인입니다. 회사에서 제공한 이메일 주소로 로그인하거나 헬프데스크에 문의하십시오.",
        already_registered:
          "(%{email})(으)로 로그인한 Adobe ID가 이미 Adobe Partner Connection 프로그램에 등록되어 있습니다. 아래 링크를 통해 로그인할 수 있습니다.",
        associated_non_partner_account:
          "(%{email})(으)로 로그인한 Adobe ID가 이미 다른 계정과 연결되어 있습니다. 다른 Adobe ID로 로그인하거나 헬프데스크에 문의하여 등록을 계속하십시오.",
        rejected_contact:
          "(%{email})(으)로 로그인한 Adobe ID의 경우 현재 Adobe Partner Connection 프로그램에 등록할 수 없습니다. 헬프데스크에 문의하십시오.",
        partial_registration:
          "연결된 계정에 대한 등록 프로세스가 아직 완료되지 않았습니다. Partner Connection 연락처 등록을 계속하기 전에 계정 등록을 완료하십시오. 등록 문제가 발생하는 경우 헬프데스크에 문의하십시오.",
        contact_helpdesk: "헬프데스크에 문의",
        apply_join: "등록 신청",
        ok: "확인",
        cbc_button_text: "Consumer & Business Connection",
        cbc_link: localizedLink("cbc_link", "ko_KR"),
        apc_button_text: "Adobe Partner Connection",
        apc_link: localizedLink("apc_link", "ko_KR"),
        apc_direct_login: apcDirectLogin(),
      },
      personal_information: {
        header: "개인 정보",
        first_name: "이름",
        last_name: "성",
        email_address: "이메일 주소",
        phone_number: "전화번호",
        phone_number_placeholder: "전화번호 입력",
        job_role: "직무",
        job_role_placeholder: "직무 선택",
        role_accounting: "회계",
        role_administration: "관리",
        role_development: "개발",
        role_executive: "경영진",
        role_finance: "금융",
        role_helpdesk_support: "헬프데스크 지원",
        role_it: "IT",
        role_marketing: "마케팅",
        role_owner: "소유자",
        role_partner_specialist: "Partner Specialist",
        role_pr_communications: "PR/커뮤니케이션",
        role_professional_services: "전문 서비스",
        role_sales_representative: "세일즈 담당자",
        role_sales_management: "세일즈 관리",
        role_system_presales_engineer: "시스템/프리세일즈 엔지니어",
        role_technical_support: "기술 지원",
        role_training: "교육",
        role_web_administration_master: "웹 관리/마스터",
        submit_button_text: "제출",
      },
      thank_you_page: {
        header: "감사합니다.",
        thank_you_public:
          "신청이 접수되어 검토 중입니다. 검토가 완료되면 이메일을 수신하게 됩니다. 함께할 수 있기를 기대합니다.",
        thank_you_corporate:
          "Adobe Partner Connection 프로그램에 등록되었습니다.",
        return_to_partner_connection: "Adobe Partner Connection으로 돌아가기",
        partner_connection_link: localizedLink("apc_link", "ko_KR"),
        questions_contact_helpdesk:
          "궁금한 사항이 있는 경우 헬프데스크에 문의하십시오.",
      },
      admin: {
        approval_header: "귀하의 승인이 필요합니다.",
        approval_instructions:
          "다음의 사용자가 APC(Adobe Partner Connection) 멤버십 등록을 요청했습니다. APC 멤버십 등록을 수락하시면 해당 사용자가 APC 파트너 포털, Adobe Consumer & Business Connection, 이메일 뉴스레터에서 파트너 전용 콘텐츠를 이용할 수 있습니다. 귀하가 이 계정의 주 연락처로 지정되어 있으므로 다음의 요청을 승인하거나 거부하면 해당 사용자에게 알림 이메일이 전송됩니다.",
        no_requests:
          "현재 검토 요청이 없습니다. 이 계정의 주 연락처로 지정된 관리자가 여러 명인 경우 다른 관리자가 이미 요청을 검토했을 수 있습니다.",
        name_label: "이름",
        email_label: "이메일",
        company_label: "회사",
        approve_button: "승인",
        reject_button: "거부",
        request_approved: "요청 승인됨",
        request_rejected: "요청 거부됨",
        done_instructions:
          "완료되면 이 창을 닫을 수 있습니다. 도움이 필요한 경우 헬프데스크에 문의하십시오.",
      },
      find_company: {
        header: "회사 찾기",
        public_domain_company_search:
          "(%{email})(으)로 로그인한 Adobe ID와 연결된 회사를 찾을 수 없습니다. 아래에서 회사를 검색하십시오.",
        company_matches:
          "로그인한 이메일 주소와 일치하는 %{count}개의 회사를 찾았습니다.",
        company_matches_1:
          "로그인한 이메일 주소와 일치하는 %{count}개의 회사를 찾았습니다.",
        no_matches_search:
          "이 검색어와 일치하는 회사를 찾을 수 없습니다. 보다 보편적인 단어로 검색하거나 헬프데스크에 문의하십시오.",
        company_name: "회사명",
        enter_company_name: "회사명 입력",
        select_company: "회사 선택",
        start_enrollment: "등록 시작",
        company_not_listed:
          '목록에 회사가 없는 경우 <a id="apply-join" href="#">신청</a>을 통해 회사를 추가할 수 있습니다.',
      },
    },
  },
  es_ES: {
    global: {
      ims: {
        sign_in: "Iniciar sesión con Adobe ID",
        sign_out: "Cerrar sesión",
      },
      header: {
        app_prefix_bold: " ",
        // app_name: 'Registro de contacto de Adobe Partner Connection',
        app_name: "Adobe Partner Connection Portal",
      },
      footer: {
        region: "Región:",
        choose_region: "Elegir una región",
        privacy: "Privacidad",
        privacy_link: localizedLink("privacy_link", "es_ES"),
        terms_of_use: "Condiciones de uso",
        terms_of_use_link: localizedLink("terms_of_use_link", "es_ES"),
        cookies: "Cookies",
        cookies_link: localizedLink("cookies_link", "es_ES"),
        help: "Servicio de asistencia",
        help_link: localizedLink("help_link", "es_ES"),
      },
      errors: {
        unable_to_load: "No se ha podido cargar la página",
        unauthorized: "Carece de la autorización para ver esta página.",
      },
    },
    workflow: {
      progress: {
        sign_in: "Iniciar sesión",
        select_company: "Seleccionar una empresa",
        personal_information: "Información personal",
        finish: "Terminar",
      },
      error_page: {
        problem_header: "Ha habido un problema.",
        general_problem:
          "Se ha producido un problema desconocido al intentar cumplimentar tu solicitud. Ponte en contacto con el servicio de asistencia para que podamos ayudarte.",
        associated_other_account:
          "El Adobe ID con el que has iniciado sesión (%{email}) ya está asociado con otro programa de socios de Adobe: %{program}. Puedes iniciar sesión con otro Adobe ID o ponerte en contacto con el servicio de asistencia para continuar con tu registro.",
        account_not_found:
          "El Adobe ID con el que has iniciado sesión (%{email}) no coincide con ninguna cuenta actual de Adobe Partner Connection. Puedes solicitar unirte al programa de Adobe Partner Connection o iniciar sesión con un Adobe ID diferente.",
        initial_search_problem:
          "Se ha producido un problema al intentar buscar tu información. Puedes actualizar la página para intentar volver a acceder a la solicitud o ponerte en contacto con el servicio de asistencia.",
        company_search_problem:
          "Se ha producido un problema al buscar los resultados de búsqueda de tu empresa. Puedes intentar la búsqueda de nuevo o ponerte en contacto con el servicio de asistencia.",
        admin_fetch_requests_problem:
          "Se ha producido un problema al intentar cargar las solicitudes de acceso pendientes. Puedes actualizar la página para intentar volver a acceder a la solicitud o ponerte en contacto con el servicio de asistencia.",
        admin_action_problem:
          "Se ha producido un problema al intentar trabajar con esta solicitud. Vuelve a intentarlo. Si continúas teniendo problemas, ponte en contacto con el servicio de asistencia.",
        submit_request_problem:
          "Se ha producido un problema al intentar enviar tu solicitud. Intenta enviar el formulario de nuevo. Si continúas teniendo problemas, ponte en contacto con el servicio de asistencia.",
        banned_domain:
          "El Adobe ID con el que has iniciado sesión (%{email}) es un dominio público que no se puede utilizar para el registro en el programa de Adobe Partner Connection. Inicia sesión con la dirección de correo electrónico que te haya proporcionado tu empresa o ponte en contacto con el servicio de asistencia para obtener ayuda.",
        already_registered:
          "El Adobe ID con el que has iniciado sesión (%{email}) ya está registrado en el programa de Adobe Partner Connection. Puedes iniciar sesión usando los siguientes enlaces.",
        associated_non_partner_account:
          "El Adobe ID con el que has iniciado sesión (%{email}) ya está asociado con otra cuenta. Inicia sesión con otro Adobe ID o ponte en contacto con el servicio de asistencia para continuar con el registro.",
        rejected_contact:
          "El Adobe ID con el que has iniciado sesión (%{email}) no puede registrarse en programa de Adobe Partner Connection en este momento. Ponte en contacto con el servicio de asistencia para obtener ayuda.",
        partial_registration:
          "La cuenta con la que estás asociado aún no ha completado el proceso de inscripción. Antes de continuar con el registro de contacto de Partner Connection, finaliza la inscripción de la cuenta. Si tienes problemas para inscribirte, ponte en contacto con el servicio de asistencia para obtener ayuda.",
        contact_helpdesk: "Contactar con el servicio de asistencia",
        apply_join: "Solicitar unirse",
        ok: "Aceptar",
        cbc_button_text: "Consumer & Business Connection",
        cbc_link: localizedLink("cbc_link", "es_ES"),
        apc_button_text: "Adobe Partner Connection",
        apc_link: localizedLink("apc_link", "es_ES"),
        apc_direct_login: apcDirectLogin(),
      },
      personal_information: {
        header: "Información personal",
        first_name: "Nombre",
        last_name: "Apellidos",
        email_address: "Dirección de correo electrónico",
        phone_number: "Número de teléfono",
        phone_number_placeholder: "Introduce tu número de teléfono",
        job_role: "Puesto de trabajo",
        job_role_placeholder: "Seleccionar un puesto de trabajo",
        role_accounting: "Contabilidad",
        role_administration: "Administración",
        role_development: "Desarrollo",
        role_executive: "Ejecutivo",
        role_finance: "Finanzas",
        role_helpdesk_support: "Servicio de asistencia técnica",
        role_it: "TI",
        role_marketing: "Marketing",
        role_owner: "Propietario",
        role_partner_specialist: "Partner Specialist",
        role_pr_communications: "PR/comunicaciones",
        role_professional_services: "Servicios profesionales",
        role_sales_representative: "Representante de ventas",
        role_sales_management: "Administración de ventas",
        role_system_presales_engineer: "Ingeniero de sistemas/preventas",
        role_technical_support: "Asistencia técnica",
        role_training: "Formación",
        role_web_administration_master: "Administración web/máster",
        submit_button_text: "Enviar",
      },
      thank_you_page: {
        header: "Gracias.",
        thank_you_public:
          "Hemos recibido tu solicitud y ahora estamos revisándola. Cuando esté revisada, recibirás un mensaje de correo electrónico. Estamos deseando trabajar contigo.",
        thank_you_corporate:
          "Te has registrado correctamente en el programa de Adobe Partner Connection.",
        return_to_partner_connection: "Volver a Adobe Partner Connection",
        partner_connection_link: localizedLink("apc_link", "es_ES"),
        questions_contact_helpdesk:
          "Si tienes alguna duda, puedes ponerte en contacto con el servicio de asistencia.",
      },
      admin: {
        approval_header: "Necesitamos tu aprobación.",
        approval_instructions:
          "Los siguientes usuarios han solicitado unirse a tu abono de Adobe Partner Connection (APC). De hacerlo, estos usuarios contarán con acceso a contenido exclusivo para socios en el portal de socios de APC, a Adobe Consumer and Business Connection y a boletines informativos por correo electrónico. Dado que eres el contacto principal de esta cuenta, te rogamos que apruebes o rechaces las siguientes solicitudes. Recibirán un correo electrónico notificándoles tu decisión.",
        no_requests:
          "En estos momentos, no tienes ninguna solicitud que revisar. Si hay varios contactos principales en esta cuenta, es posible que otra persona ya las haya revisado.",
        name_label: "Nombre",
        email_label: "Correo electrónico",
        company_label: "Empresa",
        approve_button: "Aprobar",
        reject_button: "Rechazar",
        request_approved: "Solicitud aprobada",
        request_rejected: "Solicitud rechazada",
        done_instructions:
          "Cuando se haya completado, podrás cerrar esta ventana. Si necesitas ayuda, ponte en contacto con el servicio de asistencia.",
      },
      find_company: {
        header: "Busquemos tu empresa.",
        public_domain_company_search:
          "No encontramos ninguna empresa asociada con el Adobe ID con el que has iniciado sesión (%{email}). Busca tu empresa a continuación.",
        company_matches:
          "Hemos encontrado %{count} empresas que coinciden con la dirección de correo electrónico con la que has iniciado sesión (%{email}).",
        company_matches_1:
          "Hemos encontrado %{count} empresas que coinciden con la dirección de correo electrónico con la que has iniciado sesión (%{email}).",
        no_matches_search:
          "No hemos podido encontrar ninguna empresa que coincida con esta búsqueda. Intenta ser más general o ponte en contacto con el servicio de asistencia para obtener ayuda.",
        company_name: "Nombre de la empresa",
        enter_company_name: "Introducir el nombre de la empresa",
        select_company: "Seleccionar empresa",
        start_enrollment: "Iniciar inscripción",
        company_not_listed:
          'Si no ves el nombre de tu empresa en la lista, puedes <a id="apply-join" href="#">solicitar</a> añadirla.',
      },
    },
  },
  es_LA: {
    global: {
      ims: {
        sign_in: "Iniciar sesión con Adobe ID",
        sign_out: "Cerrar sesión",
      },
      header: {
        app_prefix_bold: " ",
        // app_name: 'Registro de contacto de Adobe Partner Connection',
        app_name: "Adobe Partner Connection Portal",
      },
      footer: {
        region: "Región:",
        choose_region: "Elegir una región",
        privacy: "Privacidad",
        privacy_link: localizedLink("privacy_link", "es_LA"),
        terms_of_use: "Condiciones de uso",
        terms_of_use_link: localizedLink("terms_of_use_link", "es_LA"),
        cookies: "Cookies",
        cookies_link: localizedLink("cookies_link", "es_LA"),
        help: "Servicio de asistencia",
        help_link: localizedLink("help_link", "es_LA"),
      },
      errors: {
        unable_to_load: "No se ha podido cargar la página",
        unauthorized: "Carece de la autorización para ver esta página.",
      },
    },
    workflow: {
      progress: {
        sign_in: "Iniciar sesión",
        select_company: "Seleccionar una empresa",
        personal_information: "Información personal",
        finish: "Terminar",
      },
      error_page: {
        problem_header: "Ha habido un problema.",
        general_problem:
          "Se ha producido un problema desconocido al intentar cumplimentar tu solicitud. Ponte en contacto con el servicio de asistencia para que podamos ayudarte.",
        associated_other_account:
          "El Adobe ID con el que has iniciado sesión (%{email}) ya está asociado con otro programa de socios de Adobe: %{program}. Puedes iniciar sesión con otro Adobe ID o ponerte en contacto con el servicio de asistencia para continuar con tu registro.",
        account_not_found:
          "El Adobe ID con el que has iniciado sesión (%{email}) no coincide con ninguna cuenta actual de Adobe Partner Connection. Puedes solicitar unirte al programa de Adobe Partner Connection o iniciar sesión con un Adobe ID diferente.",
        initial_search_problem:
          "Se ha producido un problema al intentar buscar tu información. Puedes actualizar la página para intentar volver a acceder a la solicitud o ponerte en contacto con el servicio de asistencia.",
        company_search_problem:
          "Se ha producido un problema al buscar los resultados de búsqueda de tu empresa. Puedes intentar la búsqueda de nuevo o ponerte en contacto con el servicio de asistencia.",
        admin_fetch_requests_problem:
          "Se ha producido un problema al intentar cargar las solicitudes de acceso pendientes. Puedes actualizar la página para intentar volver a acceder a la solicitud o ponerte en contacto con el servicio de asistencia.",
        admin_action_problem:
          "Se ha producido un problema al intentar trabajar con esta solicitud. Vuelve a intentarlo. Si continúas teniendo problemas, ponte en contacto con el servicio de asistencia.",
        submit_request_problem:
          "Se ha producido un problema al intentar enviar tu solicitud. Intenta enviar el formulario de nuevo. Si continúas teniendo problemas, ponte en contacto con el servicio de asistencia.",
        banned_domain:
          "El Adobe ID con el que has iniciado sesión (%{email}) es un dominio público que no se puede utilizar para el registro en el programa de Adobe Partner Connection. Inicia sesión con la dirección de correo electrónico que te haya proporcionado tu empresa o ponte en contacto con el servicio de asistencia para obtener ayuda.",
        already_registered:
          "El Adobe ID con el que has iniciado sesión (%{email}) ya está registrado en el programa de Adobe Partner Connection. Puedes iniciar sesión usando los siguientes enlaces.",
        associated_non_partner_account:
          "El Adobe ID con el que has iniciado sesión (%{email}) ya está asociado con otra cuenta. Inicia sesión con otro Adobe ID o ponte en contacto con el servicio de asistencia para continuar con el registro.",
        rejected_contact:
          "El Adobe ID con el que has iniciado sesión (%{email}) no puede registrarse en programa de Adobe Partner Connection en este momento. Ponte en contacto con el servicio de asistencia para obtener ayuda.",
        partial_registration:
          "La cuenta con la que estás asociado aún no ha completado el proceso de inscripción. Antes de continuar con el registro de contacto de Partner Connection, finaliza la inscripción de la cuenta. Si tienes problemas para inscribirte, ponte en contacto con el servicio de asistencia para obtener ayuda.",
        contact_helpdesk: "Contactar con el servicio de asistencia",
        apply_join: "Solicitar unirse",
        ok: "Aceptar",
        cbc_button_text: "Consumer & Business Connection",
        cbc_link: localizedLink("cbc_link", "es_LA"),
        apc_button_text: "Adobe Partner Connection",
        apc_link: localizedLink("apc_link", "es_LA"),
        apc_direct_login: apcDirectLogin(),
      },
      personal_information: {
        header: "Información personal",
        first_name: "Nombre",
        last_name: "Apellidos",
        email_address: "Dirección de correo electrónico",
        phone_number: "Número de teléfono",
        phone_number_placeholder: "Introduce tu número de teléfono",
        job_role: "Puesto de trabajo",
        job_role_placeholder: "Seleccionar un puesto de trabajo",
        role_accounting: "Contabilidad",
        role_administration: "Administración",
        role_development: "Desarrollo",
        role_executive: "Ejecutivo",
        role_finance: "Finanzas",
        role_helpdesk_support: "Servicio de asistencia técnica",
        role_it: "TI",
        role_marketing: "Marketing",
        role_owner: "Propietario",
        role_partner_specialist: "Partner Specialist",
        role_pr_communications: "PR/comunicaciones",
        role_professional_services: "Servicios profesionales",
        role_sales_representative: "Representante de ventas",
        role_sales_management: "Administración de ventas",
        role_system_presales_engineer: "Ingeniero de sistemas/preventas",
        role_technical_support: "Asistencia técnica",
        role_training: "Formación",
        role_web_administration_master: "Administración web/máster",
        submit_button_text: "Enviar",
      },
      thank_you_page: {
        header: "Gracias.",
        thank_you_public:
          "Hemos recibido tu solicitud y ahora estamos revisándola. Cuando esté revisada, recibirás un mensaje de correo electrónico. Estamos deseando trabajar contigo.",
        thank_you_corporate:
          "Te has registrado correctamente en el programa de Adobe Partner Connection.",
        return_to_partner_connection: "Volver a Adobe Partner Connection",
        partner_connection_link: localizedLink("apc_link", "es_LA"),
        questions_contact_helpdesk:
          "Si tienes alguna duda, puedes ponerte en contacto con el servicio de asistencia.",
      },
      admin: {
        approval_header: "Necesitamos tu aprobación.",
        approval_instructions:
          "Los siguientes usuarios han solicitado unirse a tu abono de Adobe Partner Connection (APC). De hacerlo, estos usuarios contarán con acceso a contenido exclusivo para socios en el portal de socios de APC, a Adobe Consumer and Business Connection y a boletines informativos por correo electrónico. Dado que eres el contacto principal de esta cuenta, te rogamos que apruebes o rechaces las siguientes solicitudes. Recibirán un correo electrónico notificándoles tu decisión.",
        no_requests:
          "En estos momentos, no tienes ninguna solicitud que revisar. Si hay varios contactos principales en esta cuenta, es posible que otra persona ya las haya revisado.",
        name_label: "Nombre",
        email_label: "Correo electrónico",
        company_label: "Empresa",
        approve_button: "Aprobar",
        reject_button: "Rechazar",
        request_approved: "Solicitud aprobada",
        request_rejected: "Solicitud rechazada",
        done_instructions:
          "Cuando se haya completado, podrás cerrar esta ventana. Si necesitas ayuda, ponte en contacto con el servicio de asistencia.",
      },
      find_company: {
        header: "Busquemos tu empresa.",
        public_domain_company_search:
          "No encontramos ninguna empresa asociada con el Adobe ID con el que has iniciado sesión (%{email}). Busca tu empresa a continuación.",
        company_matches:
          "Hemos encontrado %{count} empresas que coinciden con la dirección de correo electrónico con la que has iniciado sesión (%{email}).",
        company_matches_1:
          "Hemos encontrado %{count} empresas que coinciden con la dirección de correo electrónico con la que has iniciado sesión (%{email}).",
        no_matches_search:
          "No hemos podido encontrar ninguna empresa que coincida con esta búsqueda. Intenta ser más general o ponte en contacto con el servicio de asistencia para obtener ayuda.",
        company_name: "Nombre de la empresa",
        enter_company_name: "Introducir el nombre de la empresa",
        select_company: "Seleccionar empresa",
        start_enrollment: "Iniciar inscripción",
        company_not_listed:
          'Si no ves el nombre de tu empresa en la lista, puedes <a id="apply-join" href="#">solicitar</a> añadirla.',
      },
    },
  },
};

for (var key of Object.keys(translations)) {
  translations[key] = Object.assign(translations[key], regTranslation[key]);
  translations[key] = Object.assign(
    translations[key],
    uplvlRenewTranslation[key],
  );
  translations[key] = Object.assign(
    translations[key],
    managePartnershipTranslation[key],
  );
  translations[key] = Object.assign(
    translations[key],
    specializationTranslation[key],
  );
}

// Any locale missing a key (e.g. a translation added only in en_US) falls
// back to the en_US value instead of rendering blank/missing in the UI.
for (var localeKey of Object.keys(translations)) {
  if (localeKey !== fallbackLocale) {
    translations[localeKey] = merge(
      {},
      translations[fallbackLocale],
      translations[localeKey],
    );
  }
}

export default translations;
